/**
 * Date formatting helpers for MediTalk study closing dates.
 *
 * Closing dates are stored as ISO `YYYY-MM-DD` strings (set by the
 * admin via a native <input type="date">). On public surfaces we
 * render them as a friendly "2nd March 2026" written form.
 *
 * Legacy data: studies created before Feb 2026 stored the closing
 * date as free text like "February 2026". The formatter detects this
 * and returns the original string unchanged so nothing breaks.
 */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Pick the right ordinal suffix for a day-of-month: 1st, 2nd, 3rd,
 * 4th… 11th, 12th, 13th… 21st, 22nd, 23rd…
 */
function ordinalSuffix(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

/**
 * Parse a free-text or ISO closing date into a written form.
 * Examples:
 *   "2026-03-02"     -> "2nd March 2026"
 *   "2026-3-2"       -> "2nd March 2026"
 *   "February 2026"  -> "February 2026"   (legacy, unchanged)
 *   ""               -> ""
 */
export function formatClosingDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  // ISO YYYY-MM-DD (or YYYY-M-D) → friendly written form
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const year = parseInt(iso[1], 10);
    const month = parseInt(iso[2], 10);
    const day = parseInt(iso[3], 10);
    if (
      Number.isFinite(year) &&
      month >= 1 && month <= 12 &&
      day >= 1 && day <= 31
    ) {
      return `${day}${ordinalSuffix(day)} ${MONTHS[month - 1]} ${year}`;
    }
  }
  return s;
}

/**
 * Inverse , when loading an existing study into the admin form, if
 * the closing date is already an ISO string we feed it straight into
 * the date input. Legacy free-text returns "" so the input shows the
 * placeholder rather than a confusing partial date.
 */
export function toIsoDateForInput(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!iso) return "";
  const year = iso[1];
  const month = String(parseInt(iso[2], 10)).padStart(2, "0");
  const day = String(parseInt(iso[3], 10)).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
